"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { fetchApi, getApiUrl } from "@/lib/api-utils";
import { NewsArticle } from "@/data/news";
import NewsCard from "@/components/shared/NewsCard";
import NewsImageSlider from "@/components/ui/NewsImageSlider";
import RichContent from "@/components/ui/RichContent";
import { getImageUrl } from "@/utils/utils";

interface StrapiNewsArticle {
  id: number;
  documentId: string;
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  publishedDate: string;
  locale: string;
  images: Array<{
    id: number;
    url: string;
    alternativeText?: string;
  }>;
  categories: { title: string; documentId: string }[];
}

interface NewsDetailsPageData {
  id: number;
  documentId: string;
  title: string;
  breadcrumb: {
    home: string;
    news: string;
  };
  allNewsText: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string;
}

interface NewsDetailClientProps {
  slug: string;
  lang: string;
  initialArticle?: NewsArticle | null;
  initialRelatedArticles?: NewsArticle[];
  initialPageData?: NewsDetailsPageData | null;
  articleError?: string | null;
}

export default function NewsDetailClient({
  slug,
  lang,
  initialArticle,
  initialRelatedArticles = [],
  initialPageData,
  articleError,
}: NewsDetailClientProps) {
  const [article, setArticle] = useState<NewsArticle | null>(initialArticle);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>(
    initialRelatedArticles
  );
  const [pageData, setPageData] = useState<NewsDetailsPageData | null>(
    initialPageData
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentNewsPage, setCurrentNewsPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only fetch if we don't have initial data
    if (!initialArticle || !initialPageData) {
      const fetchArticle = async () => {
        try {
          setLoading(true);

          // Fetch page metadata if not provided
          if (!initialPageData) {
            const pageDataUrl = getApiUrl("/news-details-page");
            const pageDataResponse = await fetchApi<{
              data: NewsDetailsPageData;
            }>(pageDataUrl);
            setPageData(pageDataResponse.data);
          }

          // Fetch the main article if not provided
          if (!initialArticle) {
            const articleUrl = getApiUrl(
              `/newses/${slug}?populate[0]=images&populate[1]=categories`
            );

            const articleResponse = await fetchApi<StrapiNewsArticle>(
              articleUrl
            );

            // Transform Strapi data to NewsArticle format
            const transformedArticle: NewsArticle = {
              id: articleResponse.id,
              slug: articleResponse.slug,
              title: articleResponse.title,
              category: articleResponse.categories?.[0]?.title,
              categoryId: articleResponse.categories?.[0]?.documentId || "",
              date: new Date(articleResponse.publishedDate).toLocaleDateString(
                "en-GB",
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }
              ),
              excerpt: articleResponse.excerpt,
              content: articleResponse.content ? [articleResponse.content] : [],
              images: articleResponse.images || [],
            };

            setArticle(transformedArticle);
          }

          // Fetch related articles if not provided
          if (!initialRelatedArticles.length) {
            const relatedUrl = getApiUrl(
              "/newses?populate[0]=images&populate[1]=categories&pageNo=1&limit=1000"
            );

            const relatedResponse = await fetchApi<{
              data: StrapiNewsArticle[];
            }>(relatedUrl);

            const transformedRelated = relatedResponse.data.map(
              (article: StrapiNewsArticle) => ({
                id: article.id,
                slug: article.slug,
                title: article.title,
                category: article.categories?.[0]?.title || "",
                categoryId: article.categories?.[0]?.documentId || "",
                date: new Date(article.publishedDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }
                ),
                excerpt: article.excerpt,
                content: article.content ? [article.content] : [],
                images:
                  article.images?.map((img) => getImageUrl(img.url)) || [],
              })
            );

            setRelatedArticles(transformedRelated);
          }
        } catch (error) {
          console.error("Error fetching article:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchArticle();
    }
  }, [slug, initialArticle, initialPageData, initialRelatedArticles]);

  // Show error state if article data fetching failed
  if (articleError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{articleError}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1a5852]"></div>
      </div>
    );
  }

  if (!article?.slug) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#3D434C] font-inter text-lg">Article not found</p>
      </div>
    );
  }

  const filteredRelatedArticles = relatedArticles.filter(
    (news) => news.id !== article.id
  );
  const newsPerPage = 3;
  const totalNewsPages = Math.ceil(
    filteredRelatedArticles.length / newsPerPage
  );
  const displayedNews = filteredRelatedArticles.slice(
    currentNewsPage * newsPerPage,
    (currentNewsPage + 1) * newsPerPage
  );

  const nextNewsPage = () => {
    setCurrentNewsPage((prev) => (prev + 1) % totalNewsPages);
  };

  const previousNewsPage = () => {
    setCurrentNewsPage((prev) => (prev - 1 + totalNewsPages) % totalNewsPages);
  };

  return (
    <div className="bg-[#f4f5f6] py-20">
      <div className="mx-auto">
        <div className="bg-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute right-[-1851px] top-[-142px] opacity-30 mix-blend-luminosity">
            <svg width="3168" height="1408" viewBox="0 0 1318 1266" fill="none">
              <g opacity="0.3" clipPath="url(#clip0_1869_3573)">
                <path
                  d="M3168.5 0.000183105H0.5V1408H3168.5V0.000183105Z"
                  fill="#F0F0F0"
                />
                <defs>
                  <clipPath id="clip0_1869_3573">
                    <rect
                      width="3168"
                      height="1408"
                      fill="white"
                      transform="translate(0.5)"
                    />
                  </clipPath>
                </defs>
              </g>
            </svg>
          </div>

          <div className="relative p-4 sm:p-6 lg:p-[50px]">
            <div className="mx-auto">
              {/* Article Header */}
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 lg:gap-0 mb-8 lg:mb-11">
                <h1 className="text-[#1a1d21] text-xl sm:text-2xl lg:text-[32px] font-medium leading-tight lg:leading-[34px] tracking-[-0.72px] max-w-[800px]">
                  {article.title}
                </h1>
                <div className="flex items-center gap-2 sm:gap-[10px] text-sm sm:text-base lg:text-lg">
                  <div className="text-[#3d434c] text-lg font-normal leading-[34px] tracking-[-0.72px]">
                    {article.date}
                  </div>
                  <div className="w-[1px] h-4 bg-[#b3b8c2]"></div>
                  <div className="text-[#3d434c] text-lg font-medium leading-[32px] tracking-[-0.72px]">
                    {article.category || "News"}
                  </div>
                </div>
              </div>

              {/* Image Section with Navigation */}
              <NewsImageSlider
                images={
                  article.images.length > 0
                    ? article.images?.map((img) => getImageUrl(img.url))
                    : []
                }
                title={article.title}
                className="w-full h-[200px] sm:h-[300px] md:h-[400px] lg:h-[540px] mb-6 rounded-2xl overflow-hidden"
              />

              {/* Article Content */}
              <div className="space-y-6 text-[#4f5662] text-base font-normal leading-7 text-justify">
                {article.content.map((paragraph: string, index: number) => (
                  <RichContent key={index} html={paragraph} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* All News Section */}
        <div className="bg-white mt-[50px] relative group/progress">
          {/* Background Image */}
          <Image
            src="/images/shared/details-bottom-bg.png"
            alt="Background pattern"
            fill
            className="object-cover object-bottom luminosity mix-blend-luminosity pointer-events-none select-none"
          />

          <div className="relative p-4 sm:p-6 lg:p-[50px]">
            {/* Section Header */}
            <div className="flex items-center gap-[10px] pb-2 border-b border-[#f4f5f6] mb-6">
              <h2 className="text-[#1a1d21] text-[28px] font-medium leading-[34px] tracking-[-0.72px]">
                {pageData?.allNewsText || "All News"}
              </h2>
            </div>

            {/* News Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6">
              {displayedNews.map((newsItem: NewsArticle) => (
                <NewsCard
                  key={newsItem.id}
                  image={newsItem.images?.[0] || ""}
                  title={newsItem.title}
                  category={newsItem.category}
                  date={newsItem?.date}
                  excerpt={newsItem.excerpt}
                  alt={newsItem.title}
                  href={`/${lang}/news/${newsItem.slug}`}
                  lang={lang}
                  variant="default"
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-6 justify-center">
              <button
                onClick={previousNewsPage}
                className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
              >
                <svg width="20" height="20" viewBox="0 0 21 20" fill="none">
                  <path
                    d="M8.4763 4.94165L3.41797 9.99998L8.4763 15.0583"
                    stroke="#88909F"
                    strokeWidth="1.25"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5836 10H3.55859"
                    stroke="#88909F"
                    strokeWidth="1.25"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={nextNewsPage}
                className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
              >
                <svg width="20" height="20" viewBox="0 0 21 20" fill="none">
                  <path
                    d="M12.5234 4.94165L17.5818 9.99998L12.5234 15.0583"
                    stroke="#88909F"
                    strokeWidth="1.25"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.41797 10H17.443"
                    stroke="#88909F"
                    strokeWidth="1.25"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex h-[2px] bg-gray-25 relative">
            <div
              className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
